import React, { useState, useEffect } from 'react'
import { Search, ChevronDown, ChevronRight, RefreshCw, User, Book, Activity, Download, AlertTriangle } from 'lucide-react'
import { useAdminApi } from '../../contexts/AdminApiContext'
import { CSVExporter } from '../../utils/csvExport'
import './CardViewer.css'

interface CardViewerProps {
  tableName: 'users' | 'books' | 'chapters' | 'api_usage'
}

const CardViewer: React.FC<CardViewerProps> = ({ tableName }) => {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set())
  const [isDownloading, setIsDownloading] = useState(false)
  const { apiService, isReady } = useAdminApi()

  useEffect(() => {
    if (isReady) {
      loadData()
    }
  }, [tableName, isReady])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      let result
      switch (tableName) {
        case 'users':
          result = await apiService.getUsers()
          break
        case 'books':
          result = await apiService.getBooks()
          break
        case 'chapters':
          result = await apiService.getChapters()
          break
        case 'api_usage':
          result = await apiService.getApiUsage()
          break
        default:
          throw new Error(`Unknown table: ${tableName}`)
      }
      
      setData(Array.isArray(result) ? result : [result])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
      console.error(`Error loading ${tableName}:`, err)
    } finally {
      setLoading(false)
    }
  }

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedCards)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedCards(newExpanded)
  }

  const getCardIcon = () => {
    switch (tableName) {
      case 'users': return <User size={20} />
      case 'books': return <Book size={20} />
      case 'chapters': return <Book size={20} />
      case 'api_usage': return <Activity size={20} />
      default: return null
    }
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString()
    } catch {
      return dateString
    }
  }

  const formatValue = (key: string, value: any): string => {
    if (value === null || value === undefined) {
      return '—'
    }
    if (typeof value === 'boolean') {
      return value ? '✓' : '✗'
    }
    if (key.includes('date') || key.includes('timestamp')) {
      return formatDate(value)
    }
    if (key.includes('cost') && typeof value === 'number') {
      return `$${value.toFixed(4)}`
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2)
    }
    return String(value)
  }

  const getMainFields = (item: any) => {
    switch (tableName) {
      case 'users':
        return {
          title: item.name || item.email,
          subtitle: item.email,
          details: [`ID: ${item.id}`, `Created: ${formatDate(item.created_at)}`]
        }
      case 'books':
        return {
          title: item.title || 'Untitled Book',
          subtitle: item.description || item.genre,
          details: [`Status: ${item.status || 'draft'}`, `Target Age: ${item.target_age || 'N/A'}`]
        }
      case 'chapters':
        return {
          title: item.title || 'Untitled Chapter',
          subtitle: item.book_title || `Book ID: ${item.book_id}`,
          details: [`Order: ${item.order_index || 'N/A'}`, item.audio_file ? `Audio: ✓` : 'Audio: —']
        }
      case 'api_usage':
        return {
          title: `${item.api_provider} - ${item.model}`,
          subtitle: item.api_endpoint,
          details: [`Cost: ${formatValue('cost', item.actual_cost_usd || item.estimated_cost_usd)}`, `Status: ${item.status}`]
        }
      default:
        return { title: 'Unknown', subtitle: '', details: [] }
    }
  }

  const filteredData = data.filter(item => {
    const searchLower = searchTerm.toLowerCase()
    const mainFields = getMainFields(item)
    return (
      mainFields.title.toLowerCase().includes(searchLower) ||
      mainFields.subtitle.toLowerCase().includes(searchLower) ||
      Object.values(item).some(value => 
        String(value).toLowerCase().includes(searchLower)
      )
    )
  })

  const handleDownloadCSV = async () => {
    if (!data.length) return
    
    setIsDownloading(true)
    try {
      // Use filtered data if search is active, otherwise all data
      const exportData = searchTerm ? filteredData : data
      
      CSVExporter.downloadCSV(exportData, tableName, {
        filename: `voyager_${tableName}`,
        includeTimestamp: true,
        // Exclude very verbose fields from CSV
        excludeColumns: ['request_metadata', 'response_metadata'],
        dateFormat: 'locale'
      })
    } catch (error) {
      console.error('Failed to download CSV:', error)
      // TODO: Add toast notification for error
    } finally {
      setIsDownloading(false)
    }
  }

  if (loading) {
    return (
      <div className="card-viewer">
        <div className="loading-state">
          <RefreshCw className="loading-spinner" size={24} />
          <span>Loading {tableName} data...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card-viewer">
        <div className="error-state">
          <span><AlertTriangle size={16} style={{ marginRight: '0.5rem' }} />Error loading {tableName}: {error}</span>
          <button className="btn btn-secondary" onClick={loadData}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="card-viewer">
      <div className="card-header">
        <div className="header-info">
          {getCardIcon()}
          <h3>{tableName.charAt(0).toUpperCase() + tableName.slice(1).replace('_', ' ')}</h3>
          <span className="record-count">{filteredData.length} records</span>
        </div>
        
        <div className="header-actions">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder={`Search ${tableName}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="action-buttons">
            <button 
              className="btn btn-secondary download-btn"
              onClick={handleDownloadCSV}
              disabled={isDownloading || !data.length}
              title={`Download ${tableName} as CSV (${CSVExporter.getEstimatedSize(data)})`}
            >
              <Download size={16} className={isDownloading ? 'downloading' : ''} />
              {isDownloading ? 'Downloading...' : 'CSV'}
            </button>
            <button className="btn btn-secondary" onClick={loadData}>
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {filteredData.length === 0 ? (
        <div className="empty-state">
          <span>No {tableName} data found</span>
        </div>
      ) : (
        <div className="cards-grid">
          {filteredData.map((item, index) => {
            const cardId = item.id || `${tableName}-${index}`
            const isExpanded = expandedCards.has(cardId)
            const mainFields = getMainFields(item)
            const allKeys = Object.keys(item)
            const hiddenKeys = ['id', 'title', 'name', 'email', 'description', 'genre', 'book_title', 'book_id']
            const detailKeys = allKeys.filter(key => !hiddenKeys.includes(key))

            return (
              <div key={cardId} className="data-card glassmorphism">
                <div className="card-main" onClick={() => toggleExpanded(cardId)}>
                  <div className="card-content">
                    <div className="card-title">{mainFields.title}</div>
                    {mainFields.subtitle && (
                      <div className="card-subtitle">{mainFields.subtitle}</div>
                    )}
                    <div className="card-details">
                      {mainFields.details.map((detail, i) => (
                        <span key={i} className="detail-badge">{detail}</span>
                      ))}
                    </div>
                  </div>
                  <div className="expand-icon">
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="card-expanded">
                    <div className="expanded-content">
                      {detailKeys.map(key => (
                        <div key={key} className="field-row">
                          <span className="field-label">
                            {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}:
                          </span>
                          <span className="field-value">
                            {formatValue(key, item[key])}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default CardViewer